import { z } from 'zod/v3';
import { zodToJsonSchema } from 'zod-to-json-schema';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JsonSchema = any;

/**
 * Recursively fix JSON Schema to ensure all objects have type field
 * Moonshot/Kimi requires every schema object to have type, anyOf, oneOf, or allOf
 */
function fixJsonSchema(schema: JsonSchema): JsonSchema {
  if (typeof schema !== 'object' || schema === null) {
    return schema;
  }

  if (Array.isArray(schema)) {
    return schema.map((item) => fixJsonSchema(item));
  }

  const result: JsonSchema = { ...schema };

  // If object has enum but no type, add type: 'string'
  if (result.enum && !result.type && !result.anyOf && !result.oneOf && !result.allOf) {
    result.type = 'string';
  }

  // If object has properties but no type, add type: 'object'
  if (result.properties && !result.type && !result.anyOf && !result.oneOf && !result.allOf) {
    result.type = 'object';
  }

  // If object has items but no type, add type: 'array'
  if (result.items && !result.type && !result.anyOf && !result.oneOf && !result.allOf) {
    result.type = 'array';
  }

  // Recursively fix nested properties
  if (result.properties) {
    const fixedProperties: JsonSchema = {};
    for (const [key, value] of Object.entries(result.properties)) {
      fixedProperties[key] = fixJsonSchema(value);
    }
    result.properties = fixedProperties;
  }

  // Recursively fix items
  if (result.items) {
    result.items = fixJsonSchema(result.items);
  }

  // Recursively fix anyOf/oneOf/allOf
  if (result.anyOf) {
    result.anyOf = result.anyOf.map((item: JsonSchema) => fixJsonSchema(item));
  }
  if (result.oneOf) {
    result.oneOf = result.oneOf.map((item: JsonSchema) => fixJsonSchema(item));
  }
  if (result.allOf) {
    result.allOf = result.allOf.map((item: JsonSchema) => fixJsonSchema(item));
  }

  return result;
}

/**
 * Convert Zod schema to standard JSON Schema for Moonshot/Kimi compatibility
 * Ensures the resulting schema has proper type fields and no empty objects
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertZodToJsonSchema = (zodSchema: z.ZodTypeAny): JsonSchema => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const jsonSchema = zodToJsonSchema(zodSchema as any, {
    target: 'openApi3',
    $refStrategy: 'none',
  });

  // Fix the schema to ensure all objects have type field
  const fixedSchema = fixJsonSchema(jsonSchema);

  // Ensure root is object (Moonshot requires object type for tool parameters)
  if (fixedSchema.type !== 'object') {
    return {
      type: 'object',
      properties: fixedSchema.properties || {},
      ...fixedSchema,
    };
  }

  return fixedSchema;
};
